import React, { useEffect, useState } from "react";
import { FolderPlus, X } from "lucide-react";

import RepertoirePopulationPermissionsEditor from "./RepertoirePopulationPermissionsEditor";

const STATUS_OPTIONS = [
    { value: "ACTIVE", label: "Actif" },
    { value: "INACTIVE", label: "Inactif" },
    { value: "LOCKED", label: "Verrouille" },
    { value: "ARCHIVED", label: "Archive" }
];

const normalizePermission = (permission) => ({
    userId: Number(permission?.userId),
    canConsult: Boolean(permission?.canConsult || permission?.canCreate || permission?.canValidate),
    canCreate: Boolean(permission?.canCreate),
    canValidate: Boolean(permission?.canValidate),
});

const buildFormState = ({ editingItem, parentItem }) => {
    if (editingItem) {
        return {
            code: editingItem.code || "",
            name: editingItem.name || "",
            description: editingItem.description || "",
            path: editingItem.path || "",
            roleId: editingItem?.roleId || "",
            parentId: editingItem.parentId || "",
            status: editingItem.status || "ACTIVE",
            filterMode: editingItem.filterMode || "IN",
            managerLevelFilter: editingItem.managerLevelFilter || "",
            entiteIds: editingItem.entiteIds || [],
            posteIds: editingItem.posteIds || [],
            userIds: editingItem.userIds || [],
            userPermissions:
                editingItem.userPermissions?.length > 0
                    ? editingItem.userPermissions.map(normalizePermission)
                    : (editingItem.authorizedUsers || []).map((user) =>
                        normalizePermission({
                            userId: user.id,
                            canConsult: true,
                            canCreate: false,
                            canValidate: false,
                        })
                    ),
        };
    }

    if (parentItem) {
        return {
            code: "",
            name: "",
            description: "",
            path: parentItem.path || "",
            roleId: parentItem?.roleId || "",
            parentId: parentItem.id,
            status: "ACTIVE",
            filterMode: parentItem.filterMode || "IN",
            managerLevelFilter: parentItem.managerLevelFilter || "",
            entiteIds: parentItem.entiteIds || [],
            posteIds: parentItem.posteIds || [],
            userIds: parentItem.userIds || [],
            userPermissions: (parentItem.userPermissions || []).map(normalizePermission),
        };
    }

    return {
        code: "",
        name: "",
        description: "",
        path: "",
        roleId: "",
        parentId: "",
        status: "ACTIVE",
        filterMode: "IN",
        managerLevelFilter: "",
        entiteIds: [],
        posteIds: [],
        userIds: [],
        userPermissions: [],
    };
};

const slugifyPathPart = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const buildHiddenPath = (form) => {
    if (String(form.path || "").trim()) return form.path.trim();
    const part = slugifyPathPart(form.code || form.name);
    if (!part) return "";
    return form.parentId ? part : `/${part}`;
};

function RepertoireFormModal({
                                 open,
                                 onClose,
                                 onSubmit,
                                 editingItem,
                                 parentItem,
                                 allUsers = [],
                                 allEntites = [],
                                 allJobs = [],
                                 flatTree = [],
                                 loading,
                             }) {
    const [form, setForm] = useState(buildFormState({ editingItem, parentItem }));
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!open) return;
        setForm(buildFormState({ editingItem, parentItem }));
        setErrors({});
    }, [open, editingItem, parentItem]);

    if (!open) return null;

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const validate = () => {
        const nextErrors = {};

        if (!String(form.code || "").trim()) nextErrors.code = "Code obligatoire";
        if (!String(form.name || "").trim()) nextErrors.name = "Nom obligatoire";

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;

        // const normalizedPermissions = (form.userPermissions || [])
        //     .filter((permission) => Number(permission.userId) > 0)
        //     .map(normalizePermission);

        const normalizedPermissions = (form.userPermissions || [])
            .filter((permission) => Number(permission.userId) > 0)
            .map(normalizePermission)
            .filter(
                (permission) =>
                    permission.canConsult || permission.canCreate || permission.canValidate
            );
        onSubmit({
            code: form.code.trim(),
            name: form.name.trim(),
            description: form.description.trim(),
            path: buildHiddenPath(form),
            roleId: form.roleId ? Number(form.roleId) : null,
            parentId: form.parentId ? Number(form.parentId) : null,
            status: form.status,
            filterMode: form.filterMode || "IN",
            managerLevelFilter: form.managerLevelFilter || null,
            entiteIds: form.entiteIds || [],
            posteIds: form.posteIds || [],
            userIds: form.userIds || [],
            userPermissions: normalizedPermissions,
            authorizedUserIds: normalizedPermissions.map((permission) => Number(permission.userId)),

            // userPermissions: normalizedPermissions,
            // authorizedUserIds: normalizedPermissions
            //     .filter(
            //         (permission) =>
            //             permission.canConsult || permission.canCreate || permission.canValidate
            //     )
            //     .map((permission) => Number(permission.userId)),
        });
    };

    return (
        <div className="rep-overlay" onMouseDown={onClose}>
            <div className="rep-modal rep-modal--wide" onMouseDown={(event) => event.stopPropagation()}>
                <div className="rep-modal__header">
                    <div className="rep-modal__title">
                        <FolderPlus size={16} style={{ color: "#dc3545" }} />
                        {editingItem
                            ? `Modifier : ${editingItem.name}`
                            : parentItem
                                ? `Ajouter un sous-repertoire a : ${parentItem.name}`
                                : "Ajouter un repertoire"}
                    </div>

                    <button type="button" className="rep-icon-btn" onClick={onClose}>
                        <X size={15} />
                    </button>
                </div>

                <div className="rep-modal__body">
                    <div className="rep-form-section">
                        <h3 className="rep-form-title">Identification</h3>

                        <div className="rep-form-grid">
                            <div className="rep-field">
                                <label>Code *</label>
                                <input
                                    value={form.code}
                                    onChange={(event) => setField("code", event.target.value)}
                                    placeholder="Ex: REP-001"
                                />
                                {errors.code && <div className="rep-error">{errors.code}</div>}
                            </div>

                            <div className="rep-field">
                                <label>Nom *</label>
                                <input
                                    value={form.name}
                                    onChange={(event) => setField("name", event.target.value)}
                                    placeholder="Nom du repertoire"
                                />
                                {errors.name && <div className="rep-error">{errors.name}</div>}
                            </div>

                            <div className="rep-field rep-field--full">
                                <label>Description</label>
                                <textarea
                                    rows={3}
                                    value={form.description}
                                    onChange={(event) => setField("description", event.target.value)}
                                    placeholder="Description..."
                                />
                            </div>

                            <div className="rep-field">
                                <label>Statut</label>
                                <select
                                    value={form.status}
                                    onChange={(event) => setField("status", event.target.value)}
                                >
                                    {STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="rep-field rep-field--full">
                                <label>Répertoire parent</label>
                                <select
                                    value={form.parentId || ""}
                                    onChange={(event) => setField("parentId", event.target.value)}
                                    disabled={!!parentItem}
                                >
                                    <option value="">Aucun</option>
                                    {flatTree
                                        .filter((item) => !editingItem || item.id !== editingItem.id)
                                        .map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {"-- ".repeat(item.__level)}
                                                {item.name}
                                            </option>
                                        ))}
                                </select>
                                {parentItem && (
                                    <div className="rep-hint">
                                        Parent impose depuis l&apos;action d&apos;ajout d&apos;un sous-repertoire.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <RepertoirePopulationPermissionsEditor
                        allUsers={allUsers}
                        allEntites={allEntites}
                        allJobs={allJobs}
                        roleId=""
                        filters={{
                            filterMode: form.filterMode,
                            managerLevelFilter: form.managerLevelFilter,
                            entiteIds: form.entiteIds,
                            posteIds: form.posteIds,
                            userIds: form.userIds,
                        }}
                        permissions={form.userPermissions}
                        onChangeFilters={(filters) =>
                            setForm((prev) => ({
                                ...prev,
                                ...filters,
                            }))
                        }
                        onChangePermissions={(userPermissions) =>
                            setForm((prev) => ({
                                ...prev,
                                userPermissions,
                            }))
                        }
                    />
                </div>

                <div className="rep-modal__footer">
                    <button type="button" className="rep-btn rep-btn--ghost" onClick={onClose}>
                        Annuler
                    </button>
                    <button
                        type="button"
                        className="rep-btn rep-btn--primary"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Enregistrement..." : editingItem ? "Enregistrer" : "Créer"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default RepertoireFormModal;
